import { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { api, resolveImageUrl } from '../lib/api';
import { Layout } from '../components/Layout';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { toast } from 'sonner';
import { BookOpen, Plus, Heart, Eye, MessageCircle, ArrowLeft, Send, Trash2, Search } from 'lucide-react';

const CATEGORIES = ['business','marketing','finance','real_estate','technology','legal','health','leadership','other'];
const CATEGORY_COLORS = {
  business:'bg-blue-100 text-blue-700', marketing:'bg-pink-100 text-pink-700', finance:'bg-green-100 text-green-700',
  real_estate:'bg-orange-100 text-orange-700', technology:'bg-cyan-100 text-cyan-700', legal:'bg-purple-100 text-purple-700',
  health:'bg-emerald-100 text-emerald-700', leadership:'bg-amber-100 text-amber-700', other:'bg-gray-100 text-gray-700'
};

const timeAgo = (d) => {
  const diff = Date.now()-new Date(d).getTime(); const m=Math.floor(diff/60000);
  if(m<1) return 'just now'; if(m<60) return `${m}m ago`;
  const h=Math.floor(m/60); if(h<24) return `${h}h ago`; return `${Math.floor(h/24)}d ago`;
};

function ArticleDetail({ article, onBack, currentUser, onDelete }) {
  const [liked, setLiked] = useState(article.liked);
  const [likes, setLikes] = useState(article.like_count||0);
  const [comment, setComment] = useState('');
  const [comments, setComments] = useState(article.comments||[]);

  const handleLike = async () => {
    try {
      const d = await api.likeArticle(article.id);
      setLiked(d.liked); setLikes(c => d.liked?c+1:c-1);
    } catch {}
  };

  const handleComment = async () => {
    if (!comment.trim()) return;
    try {
      const c = await api.commentArticle(article.id, comment);
      setComments(prev => [...prev, c]); setComment('');
    } catch { toast.error('Failed to comment'); }
  };

  return (
    <div className="max-w-3xl mx-auto space-y-6">
      <Button variant="ghost" size="sm" onClick={onBack}><ArrowLeft className="w-4 h-4 mr-1" />Back to Learning Hub</Button>
      <article>
        <div className="flex items-center gap-2 mb-2">
          <Badge className={`text-xs ${CATEGORY_COLORS[article.category]||CATEGORY_COLORS.other}`}>{article.category}</Badge>
          {article.author_id === currentUser?.id && (
            <Button size="sm" variant="ghost" className="h-6 text-destructive hover:text-destructive ml-auto" onClick={() => onDelete(article.id)}>
              <Trash2 className="w-3.5 h-3.5" />
            </Button>
          )}
        </div>
        <h1 className="text-2xl font-bold font-['Outfit'] mb-3">{article.title}</h1>
        <div className="flex items-center gap-3 mb-6 pb-4 border-b border-border">
          <div className="w-9 h-9 rounded-full bg-primary/20 flex items-center justify-center overflow-hidden text-primary text-sm font-bold">
            {article.author_photo ? <img src={resolveImageUrl(article.author_photo)} alt="" className="w-full h-full object-cover" /> : article.author_name?.[0]}
          </div>
          <div>
            <p className="text-sm font-semibold">{article.author_name}</p>
            <p className="text-xs text-muted-foreground">{article.author_profession} · {timeAgo(article.created_at)}</p>
          </div>
          <div className="ml-auto flex items-center gap-3 text-xs text-muted-foreground">
            <span className="flex items-center gap-1"><Eye className="w-3.5 h-3.5" />{article.views||0}</span>
            <span className="flex items-center gap-1"><Heart className="w-3.5 h-3.5" />{likes}</span>
          </div>
        </div>
        {article.image_url && <img src={article.image_url} alt="" className="w-full rounded-xl mb-6 max-h-64 object-cover" />}
        <div className="prose prose-sm max-w-none text-foreground/90 whitespace-pre-wrap leading-relaxed">{article.content}</div>
        {article.video_url && (
          <div className="mt-6">
            <iframe src={article.video_url} title="Video" className="w-full rounded-xl" style={{height:'315px'}} allow="autoplay; encrypted-media" allowFullScreen />
          </div>
        )}
        <div className="flex items-center gap-3 mt-6 pt-4 border-t border-border">
          <button onClick={handleLike} className={`flex items-center gap-1.5 text-sm transition-colors ${liked ? 'text-red-500' : 'text-muted-foreground hover:text-red-400'}`}>
            <Heart className={`w-4 h-4 ${liked?'fill-current':''}`} />{likes} {liked ? 'Liked':'Like'}
          </button>
          <span className="flex items-center gap-1.5 text-sm text-muted-foreground"><MessageCircle className="w-4 h-4" />{comments.length} Comments</span>
        </div>
      </article>
      {/* Comments */}
      <div className="space-y-3">
        <div className="flex gap-2">
          <Input placeholder="Write a comment..." value={comment} onChange={e => setComment(e.target.value)}
            onKeyDown={e => e.key === 'Enter' && handleComment()} />
          <Button size="icon" onClick={handleComment} disabled={!comment.trim()}><Send className="w-4 h-4" /></Button>
        </div>
        {comments.map(c => (
          <Card key={c.id}><CardContent className="p-3 flex gap-3">
            <div className="w-7 h-7 rounded-full bg-primary/20 flex items-center justify-center text-xs font-bold overflow-hidden">
              {c.user_photo ? <img src={resolveImageUrl(c.user_photo)} alt="" className="w-full h-full object-cover" /> : c.user_name?.[0]}
            </div>
            <div><p className="text-sm font-medium">{c.user_name} <span className="text-xs text-muted-foreground font-normal ml-1">{timeAgo(c.created_at)}</span></p>
            <p className="text-sm text-muted-foreground">{c.text}</p></div>
          </CardContent></Card>
        ))}
      </div>
    </div>
  );
}

export default function LearningHub() {
  const { user } = useAuth();
  const [articles, setArticles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null);
  const [showCreate, setShowCreate] = useState(false);
  const [category, setCategory] = useState('');
  const [search, setSearch] = useState('');
  const [form, setForm] = useState({ title:'', content:'', category:'business', tags:'', image_url:'', video_url:'' });
  const [submitting, setSubmitting] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const data = await api.getArticles(category||undefined, search||undefined);
      setArticles(data.articles || []);
    } catch { toast.error('Failed to load articles'); }
    finally { setLoading(false); }
  };

  useEffect(() => { load(); }, [category, search]); // eslint-disable-line

  const handleCreate = async (e) => {
    e.preventDefault();
    if (!form.title || !form.content) return toast.error('Title and content are required');
    setSubmitting(true);
    try {
      await api.createArticle({ ...form, tags: form.tags.split(',').map(t=>t.trim()).filter(Boolean) });
      toast.success('Article published!');
      setShowCreate(false);
      setForm({ title:'', content:'', category:'business', tags:'', image_url:'', video_url:'' });
      load();
    } catch(err) { toast.error(err.response?.data?.detail || 'Failed to publish'); }
    finally { setSubmitting(false); }
  };

  const handleDelete = async (articleId) => {
    if (!window.confirm('Delete this article?')) return;
    try {
      await api.deleteArticle(articleId);
      toast.success('Article deleted');
      setSelected(null);
      load();
    } catch { toast.error('Failed to delete'); }
  };

  const handleLikeCard = async (articleId, e) => {
    e.stopPropagation();
    try {
      const d = await api.likeArticle(articleId);
      setArticles(prev => prev.map(a => a.id===articleId ? {...a, liked: d.liked, like_count: a.like_count+(d.liked?1:-1)} : a));
    } catch {}
  };

  if (selected) {
    return <Layout><ArticleDetail article={selected} onBack={() => {setSelected(null);load();}} currentUser={user} onDelete={handleDelete} /></Layout>;
  }

  return (
    <Layout>
      <div className="max-w-5xl mx-auto space-y-6">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold font-['Outfit']">Learning Hub</h1>
            <p className="text-muted-foreground text-sm mt-0.5">Articles, tips and insights from the community</p>
          </div>
          <Button onClick={() => setShowCreate(true)} data-testid="create-article-btn">
            <Plus className="w-4 h-4 mr-1" />Write Article
          </Button>
        </div>

        {/* Filters */}
        <div className="flex flex-wrap gap-2 items-center">
          <div className="relative"><Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
            <Input placeholder="Search articles..." value={search} onChange={e => setSearch(e.target.value)} className="pl-9 w-48" />
          </div>
          <div className="flex flex-wrap gap-1.5">
            <Button size="sm" variant={!category?'default':'outline'} onClick={() => setCategory('')}>All</Button>
            {CATEGORIES.map(c => (
              <Button key={c} size="sm" variant={category===c?'default':'outline'} onClick={() => setCategory(c)} className="capitalize">
                {c.replace('_',' ')}
              </Button>
            ))}
          </div>
        </div>

        {/* Articles */}
        {loading ? (
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">{[1,2,3,4,5,6].map(i=><div key={i} className="h-48 rounded-xl bg-muted animate-pulse"/>)}</div>
        ) : articles.length === 0 ? (
          <Card className="border-dashed"><CardContent className="py-16 text-center text-muted-foreground">
            <BookOpen className="w-10 h-10 mx-auto mb-3 opacity-30" />
            <p className="font-medium">No articles yet</p>
            <Button className="mt-4" onClick={() => setShowCreate(true)}>Write the first article</Button>
          </CardContent></Card>
        ) : (
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
            {articles.map(a => (
              <Card key={a.id} className="cursor-pointer hover:shadow-md transition-all hover:border-primary/30 overflow-hidden" onClick={() => setSelected(a)} data-testid={`article-${a.id}`}>
                {a.image_url && <div className="h-32 overflow-hidden"><img src={a.image_url} alt="" className="w-full h-full object-cover" /></div>}
                <CardContent className="p-4">
                  <div className="flex items-center justify-between mb-2">
                    <Badge className={`text-xs ${CATEGORY_COLORS[a.category]||CATEGORY_COLORS.other}`}>{a.category}</Badge>
                  </div>
                  <h3 className="font-semibold text-sm line-clamp-2 mb-1">{a.title}</h3>
                  <p className="text-xs text-muted-foreground line-clamp-3 mb-3">{a.content?.slice(0,120)}...</p>
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-1.5 text-xs text-muted-foreground">
                      <div className="w-5 h-5 rounded-full bg-primary/20 flex items-center justify-center text-[10px] font-bold overflow-hidden">
                        {a.author_photo ? <img src={resolveImageUrl(a.author_photo)} alt="" className="w-full h-full object-cover"/> : a.author_name?.[0]}
                      </div>
                      {a.author_name?.split(' ')[0]}
                    </div>
                    <div className="flex items-center gap-2 text-xs text-muted-foreground">
                      <span className="flex items-center gap-0.5"><Eye className="w-3 h-3"/>{a.views||0}</span>
                      <button onClick={e => handleLikeCard(a.id, e)} className={`flex items-center gap-0.5 ${a.liked?'text-red-500':''}`}>
                        <Heart className={`w-3 h-3 ${a.liked?'fill-current':''}`}/>{a.like_count||0}
                      </button>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </div>

      {/* Create Article Dialog */}
      <Dialog open={showCreate} onOpenChange={setShowCreate}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle>Write an Article</DialogTitle></DialogHeader>
          <form onSubmit={handleCreate} className="space-y-4">
            <Input placeholder="Article title *" value={form.title} onChange={e=>setForm(p=>({...p,title:e.target.value}))} required />
            <Select value={form.category} onValueChange={v=>setForm(p=>({...p,category:v}))}>
              <SelectTrigger><SelectValue placeholder="Category" /></SelectTrigger>
              <SelectContent>{CATEGORIES.map(c=><SelectItem key={c} value={c} className="capitalize">{c.replace('_',' ')}</SelectItem>)}</SelectContent>
            </Select>
            <Textarea placeholder="Write your article content here... Share your insights, tips, or experiences." value={form.content} onChange={e=>setForm(p=>({...p,content:e.target.value}))} rows={10} required />
            <Input placeholder="Cover image URL (optional)" value={form.image_url} onChange={e=>setForm(p=>({...p,image_url:e.target.value}))} />
            <Input placeholder="Video URL - YouTube embed (optional)" value={form.video_url} onChange={e=>setForm(p=>({...p,video_url:e.target.value}))} />
            <Input placeholder="Tags (comma-separated)" value={form.tags} onChange={e=>setForm(p=>({...p,tags:e.target.value}))} />
            <div className="flex gap-2 justify-end">
              <Button type="button" variant="outline" onClick={()=>setShowCreate(false)}>Cancel</Button>
              <Button type="submit" disabled={submitting} data-testid="submit-article-btn">{submitting?'Publishing...':'Publish Article'}</Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </Layout>
  );
}
